import React, { useState } from 'react';
import { Grid, Box, IconButton, Typography, Switch, FormControlLabel } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import InputField from 'components/formFields/InputField';
import AutocompleteField from 'components/formFields/autocompleteField';
import { InfoModal } from 'components/generics/InfoModal';
import { useField } from 'formik';

interface Props {
  formField: any;
  servicesOptions: { value: string; label: string }[];
  skillsOptions?: { id: string; name: string }[];
}

const visibilityOptions = [
  { value: 'private', label: 'Privado' },
  { value: 'public', label: 'Público' },
  { value: 'internal', label: 'Interno' }
];

export const AsistentesForm = ({ formField, servicesOptions, skillsOptions }: Props) => {
  const {
    name,
    description,
    service_id,
    systemPrompt,
    welcomeMessage,
    temperature,
    topP,
    maxTokens,
    avatar,
    icon,
    color,
    visibility,
    isPublic,
    allowHistory,
    allowImages,
    allowAudio,
    allowFileUpload,
    maxMessagesPerDay,
    maxContextMessages,
    assignedSkills
  } = formField;

  const [activeModal, setActiveModal] = useState<string | null>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'name': return { title: 'Nombre', description: 'Nombre del Asistente.', infoText: 'Dale un nombre fácil de reconocer.' };
      case 'description': return { title: 'Descripción', description: 'Descripción del Asistente.', infoText: 'Explica brevemente qué hará este asistente.' };
      case 'service_id': return { title: 'Servicio IA', description: 'Modelo IA base.', infoText: 'Elige el "cerebro" que usará este asistente.' };
      case 'systemPrompt': return { title: 'System Prompt', description: 'Instrucciones principales.', infoText: 'Instrucciones sobre cómo debe comportarse el asistente.' };
      case 'welcomeMessage': return { title: 'Mensaje de Bienvenida', description: 'Mensaje inicial del asistente.', infoText: 'El mensaje con el que el asistente saludará al usuario.' };
      case 'temperature': return { title: 'Temperatura', description: 'Creatividad de las respuestas.', infoText: 'Si es cercano a 0, será directo. Cercano a 2, más creativo.' };
      case 'topP': return { title: 'Top P', description: 'Variedad de palabras.', infoText: 'Similar a la temperatura, ayuda a la diversidad del texto.' };
      case 'maxTokens': return { title: 'Máximo de Tokens', description: 'Límite de longitud de la respuesta.', infoText: 'Controla qué tan largas serán las respuestas.' };
      case 'avatar': return { title: 'Avatar', description: 'URL de la imagen del avatar.', infoText: 'URL de una imagen para representar al asistente.' };
      case 'icon': return { title: 'Icono', description: 'Nombre del icono.', infoText: 'Icono a mostrar en la interfaz.' };
      case 'color': return { title: 'Color', description: 'Color principal.', infoText: 'Color distintivo del asistente (ej. #FF5733).' };
      case 'visibility': return { title: 'Visibilidad', description: 'Nivel de visibilidad.', infoText: 'Determina quién puede ver y usar este asistente.' };
      case 'isPublic': return { title: 'Es Público', description: 'Acceso público.', infoText: 'Si el asistente es accesible públicamente.' };
      case 'allowHistory': return { title: 'Permitir Historial', description: 'Guardar conversaciones.', infoText: 'Si el asistente guarda el historial del chat.' };
      case 'allowImages': return { title: 'Permitir Imágenes', description: 'Procesamiento de imágenes.', infoText: 'Si el asistente puede recibir y procesar imágenes.' };
      case 'allowAudio': return { title: 'Permitir Audio', description: 'Procesamiento de audio.', infoText: 'Si el asistente puede recibir y procesar audio.' };
      case 'allowFileUpload': return { title: 'Subir Archivos', description: 'Recepción de archivos.', infoText: 'Si el asistente permite la subida de documentos o archivos.' };
      case 'maxMessagesPerDay': return { title: 'Mensajes por Día', description: 'Límite diario.', infoText: 'Cantidad máxima de mensajes permitidos por día.' };
      case 'maxContextMessages': return { title: 'Mensajes en Contexto', description: 'Historial en memoria.', infoText: 'Cuántos mensajes anteriores se envían como contexto a la IA.' };
      default: return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        {field}
      </Box>
      <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
        <InfoOutlined />
      </IconButton>
    </Box>
  );

  const CustomSwitch = ({ fieldConfig, modalKey }: { fieldConfig: any; modalKey: string }) => {
    const [field, , helpers] = useField(fieldConfig.name);
    const isActive = field.value;
    
    return (
      <Box sx={{ 
          p: 1.5, 
          borderRadius: 3, 
          border: '1px solid', 
          borderColor: isActive ? 'primary.main' : 'divider',
          background: isActive ? (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}15 100%)` : 'background.paper',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: isActive ? '0px 4px 12px rgba(107, 76, 255, 0.15)' : 'none',
          '&:hover': {
              borderColor: 'primary.light',
              transform: 'translateY(-2px)'
          }
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!isActive}
              onChange={(e) => helpers.setValue(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography sx={{ fontWeight: isActive ? 600 : 400, transition: '0.2s' }}>{fieldConfig.label}</Typography>}
          sx={{ m: 0, flexGrow: 1 }}
        />
        <IconButton size="small" color="info" onClick={() => setActiveModal(modalKey)}>
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs:12, sm:6 }}>
          {renderFieldWithInfo(<InputField name={name.name} label={name.label} fullWidth />, 'name')}
        </Grid>
        <Grid size={{ xs:12, sm:6 }} >
          {renderFieldWithInfo(<AutocompleteField name={service_id.name} label={service_id.label} options={servicesOptions} fullWidth />, 'service_id')}
        </Grid>
        <Grid size={{ xs:12 }}>
          {renderFieldWithInfo(<InputField name={description.name} label={description.label} fullWidth multiline minRows={2} />, 'description')}
        </Grid>
        <Grid size={{ xs:12 }}>
          {renderFieldWithInfo(<InputField name={systemPrompt.name} label={systemPrompt.label} fullWidth multiline minRows={3} />, 'systemPrompt')}
        </Grid>
        <Grid size={{ xs:12 }}>
          {renderFieldWithInfo(<InputField name={welcomeMessage.name} label={welcomeMessage.label} fullWidth multiline minRows={2} />, 'welcomeMessage')}
        </Grid>
        
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={temperature.name} label={temperature.label} fullWidth type="number" inputProps={{ step: "0.1", min: "0", max: "2" }} />, 'temperature')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={topP.name} label={topP.label} fullWidth type="number" inputProps={{ step: "0.1", min: "0", max: "1" }} />, 'topP')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={maxTokens.name} label={maxTokens.label} fullWidth type="number" />, 'maxTokens')}
        </Grid>

        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={avatar.name} label={avatar.label} fullWidth />, 'avatar')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={icon.name} label={icon.label} fullWidth />, 'icon')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={color.name} label={color.label} fullWidth type="color" />, 'color')}
        </Grid>

        <Grid size={{ xs:12, sm:6 }}>
           {renderFieldWithInfo(<AutocompleteField name={visibility.name} label={visibility.label} options={visibilityOptions} fullWidth />, 'visibility')}
        </Grid>
        <Grid size={{ xs:12, sm:3 }}>
          {renderFieldWithInfo(<InputField name={maxMessagesPerDay.name} label={maxMessagesPerDay.label} fullWidth type="number" />, 'maxMessagesPerDay')}
        </Grid>
        <Grid size={{ xs:12, sm:3 }}>
          {renderFieldWithInfo(<InputField name={maxContextMessages.name} label={maxContextMessages.label} fullWidth type="number" />, 'maxContextMessages')}
        </Grid>
        
        <Grid size={{ xs:12 }}>
          <Box sx={{ mt: 3, mb: 1, p: 2, borderRadius: 2, background: 'linear-gradient(90deg, rgba(107,76,255,0.05) 0%, rgba(0,0,0,0) 100%)', borderLeft: '4px solid #6b4cff' }}>
             <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(45deg, #6b4cff, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                ⚙️ Capacidades y Permisos
             </Typography>
             <Typography variant="body2" color="textSecondary">
                Configura los permisos y capacidades del asistente.
             </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={isPublic} modalKey="isPublic" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={allowHistory} modalKey="allowHistory" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={allowImages} modalKey="allowImages" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={allowAudio} modalKey="allowAudio" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={allowFileUpload} modalKey="allowFileUpload" />
        </Grid>

        {skillsOptions && skillsOptions.length > 0 && (
          <Grid size={{ xs:12 }}>
            <Box sx={{ mt: 3, mb: 1, p: 2, borderRadius: 2, background: 'linear-gradient(90deg, rgba(107,76,255,0.05) 0%, rgba(0,0,0,0) 100%)', borderLeft: '4px solid #6b4cff' }}>
               <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(45deg, #6b4cff, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  🧠 Habilidades del Asistente (Skills)
               </Typography>
               <Typography variant="body2" color="textSecondary">
                  Selecciona las habilidades y permisos que este asistente puede ejecutar.
               </Typography>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {skillsOptions.map((skill) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={skill.id}>
                    <CustomSwitch 
                       fieldConfig={{ name: `${assignedSkills.name}[${skill.id}]`, label: skill.name }} 
                       modalKey="assignedSkills" 
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        )}

      </Grid>
      <InfoModal
        open={activeModal !== null}
        setOpen={(val: boolean) => { if (!val) setActiveModal(null); }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </>
  );
};
